/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useState } from 'react';

import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { Checkbox } from '@atlaskit/checkbox/checkbox';
import { cssMap, jsx } from '@atlaskit/css';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import ThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import TextArea from '@atlaskit/textarea/text-area';
import { token } from '@atlaskit/tokens';
import { PanelDisclaimer } from '@atlassian/panel-system/panel-disclaimer';
import { PanelFooter } from '@atlassian/panel-system/panel-footer';

// Each footer variation is wrapped in a bordered box purely for presentation in the docs.
// In a real panel, render `PanelFooter` directly inside a `PanelContainer`.
const styles = cssMap({
	gallery: {
		width: '400px',
	},
	footerWrapper: {
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderRadius: token('radius.small'),
		overflow: 'hidden',
	},
});

function FooterVariant({ children }: { children: React.ReactNode }) {
	return <Box xcss={styles.footerWrapper}>{children}</Box>;
}

function CommentComposer() {
	const [comment, setComment] = useState('');

	return (
		<Stack space="space.150" grow="fill">
			<TextArea
				value={comment}
				onChange={(event) => setComment(event.currentTarget.value)}
				minimumRows={3}
				aria-label="Add a comment"
			/>
			<Flex justifyContent="space-between" alignItems="center">
				<Checkbox label="Watch this content" />
				<Button appearance="primary" onClick={() => setComment('')}>
					Add comment
				</Button>
			</Flex>
		</Stack>
	);
}

export default function Footers(): React.JSX.Element {
	return (
		<Stack space="space.300" xcss={styles.gallery}>
			{/* Right aligned buttons */}
			<FooterVariant>
				<PanelFooter>
					<ButtonGroup label="Panel actions">
						<Button appearance="subtle" onClick={() => {}}>
							Secondary action
						</Button>
						<Button appearance="primary" onClick={() => {}}>
							Primary action
						</Button>
					</ButtonGroup>
				</PanelFooter>
			</FooterVariant>

			{/* Spread out buttons */}
			<FooterVariant>
				<PanelFooter>
					<Button appearance="primary" onClick={() => {}}>
						Primary action
					</Button>
					<Button appearance="default" onClick={() => {}}>
						Secondary action
					</Button>
				</PanelFooter>
			</FooterVariant>

			{/* Footer + disclaimer bar (1 zone) */}
			<FooterVariant>
				<PanelFooter
					disclaimer={
						<PanelDisclaimer>
							<Flex justifyContent="center">
								<Text size="small" color="color.text.subtle">
									Powered by Rovo AI. Verify results.
								</Text>
							</Flex>
						</PanelDisclaimer>
					}
				>
					<Button appearance="primary" onClick={() => {}}>
						Primary action
					</Button>
					<Checkbox label="Label" />
				</PanelFooter>
			</FooterVariant>

			{/* Footer + disclaimer bar (2 zones) */}
			<FooterVariant>
				<PanelFooter
					disclaimer={
						<PanelDisclaimer>
							<Flex justifyContent="space-between" alignItems="center">
								<Inline space="space.025">
									<IconButton
										icon={ThumbsUpIcon}
										label="Helpful"
										appearance="subtle"
										spacing="compact"
										onClick={() => {}}
									/>
									<IconButton
										icon={ThumbsDownIcon}
										label="Not helpful"
										appearance="subtle"
										spacing="compact"
										onClick={() => {}}
									/>
								</Inline>
								<Text size="small" color="color.text.subtle">
									Powered by Rovo AI. Verify results.
								</Text>
							</Flex>
						</PanelDisclaimer>
					}
				>
					<Button appearance="primary" onClick={() => {}}>
						Primary action
					</Button>
					<Checkbox label="Label" />
				</PanelFooter>
			</FooterVariant>

			{/* Footer open slot */}
			<FooterVariant>
				<PanelFooter>
					<CommentComposer />
				</PanelFooter>
			</FooterVariant>
		</Stack>
	);
}
